import {
  Box,
  Checkbox,
  Grid,
  IconButton,
  Link,
  makeStyles,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableRow,
  TextField,
  Theme,
  Typography
} from "@material-ui/core";
import React, { useEffect, useState } from "react";
import { formatTimestamp, boxShadow, Loader, useAuth } from "swyft-react-core";
import { Account, EQuotePurchaserType } from "../../api/Types";
import { Quote } from "../../api/Quote/Quote";
import { PermissionQuoteApprovedLineItemWrite, PermissionQuoteLineItemWrite } from "../../app/consts";
import { allowAdminEdits, allowManagerEdits } from "../../app/utils";
import {
  setGrantValue,
  toggleGraphVisibility,
  updateQuoteLeadSource,
  updateQuotePurchaserType
} from "../../api/Quote/QuoteApi";
import { fetchQuote, fetchSolarReport, toggleQuoteGraphVisibility } from "../../store/QuoteSlice";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { toNumber } from "lodash";
import SaveIcon from "@material-ui/icons/Save";
import OpenInNewIcon from "@material-ui/icons/OpenInNew";
import { RootState } from "../../app/rootReducer";
import { PriceFormat } from "../../components/typography/PriceFormat";

const useStyles = makeStyles((theme: Theme) => ({
  root: {},
  table: {
    "& td": {
      borderBottom: 0,
      verticalAlign: "top",
      paddingLeft: "0"
    },
    "& td:first-of-type": {
      fontWeight: "bold",
      width: "200px"
    }
  },
  formControl: {
    margin: theme.spacing(1),
    minWidth: 120
  }
}));

interface Props {
  quote: Quote;
  account: Account;
}

const leadSourceLocalHeroes: string = "Local Heroes";
const leadSourceOther: string = "Other";
const leadSources: string[] = [leadSourceLocalHeroes, leadSourceOther];

export const CustomerDetails: React.FC<Props> = ({ quote, account }: Props) => {
  const classes = useStyles();
  const { isAuthenticated, isLoading: authIsLoading, hasPermission } = useAuth();

  const [saving, setSaving] = useState(false);
  const { solarReport, balance, balanceError } = useSelector((state: RootState) => state.quote);
  const [grantAmount, setGrantAmount] = useState(solarReport?.grantAmount ?? 0);
  const dispatch = useDispatch();

  const isSolar = quote?.type?.includes("Solar");

  useEffect(() => {
    if (isSolar) {
      dispatch(fetchSolarReport({ quoteId: quote?.id, includeBattery: quote.addons?.some((addon) => addon.selected) }));
    }
  }, [dispatch, isSolar, quote]);

  if (!account || authIsLoading) {
    return <Loader />;
  }

  var accountAddress = account.addresses.length > 0 ? account.addresses[0] : null;

  const xeroContactId = account.externalIds?.find((externalId) => externalId.serviceName === "Xero")?.serviceIds[0];

  const handleChangePurchaserType = async (purchaserType: EQuotePurchaserType) => {
    if (saving) {
      return;
    }

    setSaving(true);

    await updateQuotePurchaserType(quote.id, purchaserType)
      .then(() => {
        dispatch(fetchQuote(quote.id));
      })
      .catch((err) => {
        console.log(err);
      })
      .finally(() => {
        setSaving(false);
      });
  };

  const handleChangeLeadSource = async (leadSource: string) => {
    if (saving) {
      return;
    }

    setSaving(true);

    await updateQuoteLeadSource(quote.id, leadSource === leadSourceLocalHeroes ? leadSource : leadSourceOther)
      .then(() => {
        dispatch(fetchQuote(quote.id));
      })
      .catch((err) => {
        console.log(err);
      })
      .finally(() => {
        setSaving(false);
      });
  };

  const handleToggleGraphVisibility = async () => {
    if (saving) {
      return;
    }
    setSaving(true);
    await toggleGraphVisibility(quote.id)
      .then(() => {
        dispatch(toggleQuoteGraphVisibility(!quote.isGraphVisible));
      })
      .then((_) => {
        toast.success("Visibility Updated");
      })
      .finally(() => {
        setSaving(false);
      });
  };

  const handleSetGrantValue = async () => {
    if (saving) {
      return;
    }
    setSaving(true);
    await setGrantValue(quote.assessmentId, grantAmount)
      .then(() => {
        dispatch(fetchQuote(quote.id));
        dispatch(fetchSolarReport({ quoteId: quote.id }));
      })
      .then((_) => {
        toast.success("Grant Updated");
      })
      .finally(() => {
        setSaving(false);
      });
  };

  if (saving) {
    return <Loader />;
  }

  return (
    <Box boxShadow={boxShadow} padding={3} mb={3}>
      <Typography variant="h2" paragraph>
        Customer Details
      </Typography>
      <Grid container spacing={0}>
        <Grid item xs={6}>
          <Table size="small" className={classes.table}>
            <TableBody>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>{account.name}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>Address</TableCell>
                <TableCell>
                  {accountAddress && (
                    <React.Fragment>
                      <div>{accountAddress.addressLine1}</div>
                      <div>{accountAddress.addressLine2}</div>
                      <div>{accountAddress.addressLine3}</div>
                      <div>{accountAddress.townCity}</div>
                      <div>{accountAddress.county}</div>
                    </React.Fragment>
                  )}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>Eircode</TableCell>
                <TableCell>{accountAddress && accountAddress.postCode}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>Email</TableCell>
                <TableCell>{account.email}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>Phone</TableCell>
                <TableCell>{account.mobilePhone}</TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </Grid>
        <Grid item xs={6}>
          <Table size="small" className={classes.table}>
            <TableBody>
              <TableRow>
                <TableCell>Quote Ref</TableCell>
                <TableCell>{quote.name}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>Swyft ID</TableCell>
                <TableCell>{quote.swyftId}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>Status</TableCell>
                <TableCell>{quote.status}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>Balance</TableCell>
                <TableCell>
                  {balanceError ?? (balance?.balance && <PriceFormat value={balance?.balance ?? 0} />) ?? "Unknown"}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>Date</TableCell>
                <TableCell>{formatTimestamp(quote.created)}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>Expiry</TableCell>
                <TableCell>{formatTimestamp(quote.expiry || quote.created + 30 * 24 * 60 * 60 * 1000)}</TableCell>
              </TableRow>
              {quote.xeroInvoiceId && (
                <TableRow>
                  <TableCell>Xero</TableCell>
                  <TableCell>
                    <Link
                      href={`https://go.xero.com/AccountsReceivable/View.aspx?InvoiceID=${quote.xeroInvoiceId}`}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Invoice
                      <OpenInNewIcon fontSize="small" style={{ marginLeft: 4 }} />
                    </Link>
                  </TableCell>
                </TableRow>
              )}
              {xeroContactId && (
                <TableRow>
                  <TableCell>Xero Contact</TableCell>
                  <TableCell>
                    <Link
                      href={`https://go.xero.com/Contacts/View.aspx?ContactID=${xeroContactId}`}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Xero Contact
                      <OpenInNewIcon fontSize="small" style={{ marginLeft: 4 }} />
                    </Link>
                  </TableCell>
                </TableRow>
              )}
              <TableRow>
                <TableCell>Referral Code</TableCell>
                <TableCell>{quote.referralCode}</TableCell>
              </TableRow>
              {isSolar && (
                <>
                  <TableRow>
                    <TableCell>Graph Visible</TableCell>
                    <TableCell>
                      <Checkbox
                        checked={quote.isGraphVisible}
                        onChange={handleToggleGraphVisibility}
                        disabled={!isAuthenticated || !hasPermission(PermissionQuoteLineItemWrite)}
                        color="secondary"
                        inputProps={{ "aria-label": "secondary checkbox" }}
                        style={{ padding: 0 }}
                      />
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>Property Type</TableCell>
                    {isAuthenticated &&
                    ((hasPermission(PermissionQuoteApprovedLineItemWrite) && allowAdminEdits(quote)) ||
                      (hasPermission(PermissionQuoteLineItemWrite) && allowManagerEdits(quote))) ? (
                      <TableCell>
                        <Select
                          id="select-purchaser-type-select"
                          onChange={(e: any) => handleChangePurchaserType(e.target.value)}
                          value={quote.purchaserType}
                        >
                          {Object.values(EQuotePurchaserType).map((value) => {
                            return (
                              <MenuItem key={value} value={value}>
                                {value}
                              </MenuItem>
                            );
                          })}
                        </Select>
                      </TableCell>
                    ) : (
                      <TableCell>{quote.purchaserType}</TableCell>
                    )}
                  </TableRow>
                  {isAuthenticated &&
                    ((hasPermission(PermissionQuoteApprovedLineItemWrite) && allowAdminEdits(quote)) ||
                      (hasPermission(PermissionQuoteLineItemWrite) && allowManagerEdits(quote))) &&
                    quote.purchaserType === EQuotePurchaserType.Farm && (
                      <TableRow>
                        <TableCell>TAMS Grant</TableCell>
                        <TableCell>
                          <TextField
                            id="amount"
                            label="Amount"
                            variant="outlined"
                            type="number"
                            value={grantAmount}
                            onChange={(e) => {
                              const newValue = toNumber(e.currentTarget.value);
                              if (newValue >= 0) {
                                setGrantAmount(newValue);
                              }
                            }}
                            inputProps={{
                              min: "0"
                            }}
                          />
                          <IconButton
                            edge="start"
                            color="primary"
                            aria-label="menu"
                            onClick={handleSetGrantValue}
                            disabled={solarReport?.grantAmount === grantAmount}
                          >
                            <SaveIcon color={solarReport?.grantAmount === grantAmount ? "disabled" : "secondary"} />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    )}
                </>
              )}
              {isAuthenticated && !isSolar && (
                <TableRow>
                  <TableCell>Lead Source</TableCell>
                  {hasPermission(PermissionQuoteLineItemWrite) && allowManagerEdits(quote) ? (
                    <TableCell>
                      <Select
                        id="select-lead-source-select"
                        onChange={(e: any) => handleChangeLeadSource(e.target.value)}
                        value={
                          (quote?.leadSource === leadSourceLocalHeroes ? quote.leadSource : leadSourceOther) ??
                          leadSourceOther
                        }
                      >
                        {leadSources.map((value) => {
                          return (
                            <MenuItem key={value} value={value}>
                              {value}
                            </MenuItem>
                          );
                        })}
                      </Select>
                    </TableCell>
                  ) : (
                    <TableCell>{quote.leadSource ?? leadSourceOther}</TableCell>
                  )}
                </TableRow>
              )}
            </TableBody>
          </Table>
        </Grid>
      </Grid>
    </Box>
  );
};
